import { ReactNode, useCallback, useMemo } from 'react';
import { QueryClientProvider, ProductCardActionsContext } from '@socialflows/shared-lib';
import { Toaster } from '@/components/ui/toaster';
import { ThemeProvider } from '@/components/theme-provider';
import { AuthProvider } from '@/contexts/auth-context';
import { AccountProvider } from '@/contexts/account-context';
import { WishlistProvider, useWishlist } from '@/contexts/wishlist-context';
import { CartProvider, useCart } from '@/contexts/cart-context';
import { PresetCategoriesProvider } from '@/contexts/preset-categories-context';
import { StoreLayout } from '@/src/components/store-layout';

function ProductCardActionsBridge({ children }: { children: ReactNode }) {
  const { isInWishlist, isWishlistLoading, toggleWishlist } = useWishlist();
  const { addToCart } = useCart();

  const actions = useMemo(
    () => ({
      isWishlisted: isInWishlist,
      isWishlistLoading,
      onToggleWishlist: toggleWishlist,
      onAddToCart: (product: Parameters<typeof addToCart>[0]) => addToCart(product, 1),
    }),
    [isInWishlist, isWishlistLoading, toggleWishlist, addToCart]
  );

  return <ProductCardActionsContext.Provider value={actions}>{children}</ProductCardActionsContext.Provider>;
}

export function ClientProviders({ children }: { children: ReactNode }) {
  return (
    <QueryClientProvider>
      <ThemeProvider theme={null}>
        <AuthProvider>
          <AccountProvider>
            <WishlistProvider>
              <CartProvider>
                <PresetCategoriesProvider>
                  <ProductCardActionsBridge>
                    <StoreLayout>{children}</StoreLayout>
                  </ProductCardActionsBridge>
                </PresetCategoriesProvider>
              </CartProvider>
            </WishlistProvider>
          </AccountProvider>
        </AuthProvider>
      </ThemeProvider>
      <Toaster />
    </QueryClientProvider>
  );
}
